import { pgEnum } from 'drizzle-orm/pg-core'

/**
 * Source of a seat entitlement. Split into two families:
 * - Grant family (recipient pays nothing; totalValue is the impact value; feeds impact/sponsor reporting):
 *   `sponsored_scholarship`, `free_trial`, `pilot`, `internal`, `pemda`.
 * - Revenue family (offline-paid; totalValue is the price paid; linked to an internal invoice;
 *   excluded from all impact/sponsor reporting): `direct_paid`.
 */
export const SeatSourceTypeEnum = pgEnum('seat_source_type', [
  'sponsored_scholarship',
  'free_trial',
  'pilot',
  'internal',
  'pemda',
  'direct_paid',
])
export type SeatSourceType = (typeof SeatSourceTypeEnum.enumValues)[number]

/**
 * The grant family: the recipient pays nothing. `direct_paid` is deliberately absent — it is offline
 * revenue, so a `direct_paid` pool counts as a commercial one.
 *
 * `@package/analytics` keeps its own copy of this list for event typing; it cannot import from here
 * without taking a dependency on the schema package. Keep the two in step.
 */
export const GRANT_FAMILY_SOURCE_TYPES = [
  'sponsored_scholarship',
  'free_trial',
  'pilot',
  'internal',
  'pemda',
] as const satisfies readonly SeatSourceType[]

/** True when the recipient paid nothing for the seats — drives the "belum premium" commercial state. */
export function isGrantFamily(sourceType: SeatSourceType): boolean {
  return (GRANT_FAMILY_SOURCE_TYPES as readonly SeatSourceType[]).includes(sourceType)
}

export const SeatEntitlementStatusEnum = pgEnum('seat_entitlement_status', ['draft', 'active', 'expired', 'revoked'])
export type SeatEntitlementStatus = (typeof SeatEntitlementStatusEnum.enumValues)[number]

export const ProgramStatusEnum = pgEnum('program_status', ['draft', 'active', 'expired', 'revoked'])
export type ProgramStatus = (typeof ProgramStatusEnum.enumValues)[number]

export const FunderTypeEnum = pgEnum('funder_type', ['corporate', 'foundation', 'government', 'internal', 'individual'])
export type FunderType = (typeof FunderTypeEnum.enumValues)[number]
